// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE CODE PERSISTÉ D'UNE PIÈCE FISCALE — une seule déclaration pour cinq producteurs et six lecteurs.
//
// ⚠️ 🔴 POURQUOI CE MODULE EXISTE. Une pièce porte son type DEUX FOIS, sous deux formes : le mot de la
// charge SIGNÉE (`ticket`, `facture`, `avoir`…) et le CODE de la colonne `fiscal_piece.piece_type`
// (`TIK`, `FAC`, `AVO`…). Rien ne déclarait leur correspondance : elle était implicite dans **deux
// ternaires voisins** du même fichier de scellement —
//     const typePiece = asFacture ? 'facture' : isRefund ? 'avoir' : 'ticket';   // ce qui est SIGNÉ
//     pieceType:       asFacture ? 'FAC'     : isRefund ? 'AVO'   : 'TIK',      // ce qui est STOCKÉ
// — qui doivent dire la même chose et que rien n'obligeait à le faire. Relevé : **cinq producteurs**
// (trois sur la caisse, un à la Console pour la reprise) et **six lecteurs** (back-office serveur et
// web), chacun avec ses propres littéraux.
//
// ⚠️ 🔴 CE QUE LA DIVERGENCE COÛTERAIT, ET POURQUOI ELLE SERAIT MUETTE : le code stocké ne sert qu'aux
// LECTEURS (écrans, statistiques, requêtes), le mot signé n'entre que dans la SIGNATURE. Les deux ne se
// croisent jamais à l'exécution — donc une pièce scellée « facture » et stockée `TIK` serait
// parfaitement valide, signée, chaînée, et invisible à tous les contrôles, tout en manquant à la
// première liste de factures.
//
// ⚠️ 🔴 LES CODES NE SE RENOMMENT PAS (Forme J) : ils sont persistés dans des pièces SIGNÉES et
// append-only. Ce module protège leur DÉCLARATION UNIQUE, jamais leur valeur. Les mots de la charge
// signée non plus, pour la même raison — ils entrent dans la signature.
//
// ⚠️ IL VIT AU CŒUR parce que le cœur possède déjà les DEUX vocabulaires : `PieceType` /
// `OperationType` (la charge signée) et le schéma partagé (la colonne). Le poser ailleurs ferait d'un
// paquet tiers l'arbitre d'une correspondance dont le cœur porte les deux moitiés.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import type { OperationType, PieceType } from './serialize';
import { isInvoiceSeriesNumber } from './sequence';

/**
 * LE CODE PERSISTÉ DE CHAQUE TYPE DE PIÈCE QU'UN PRODUCTEUR SCELLE.
 *
 * ⚠️ 🔴 QUATRE ENTRÉES, PAS SIX, ET C'EST MESURÉ. `PieceType` compte six membres, mais seuls quatre
 * sont réellement scellés aujourd'hui : `ticket`/`facture` (vente), `avoir` (remboursement au
 * comptoir), `justificatif` (changement de moyen de paiement). `note` et `titre` n'ont **aucun
 * producteur** — leur inventer un code ici serait décider seul d'une valeur qui entrerait ensuite dans
 * des pièces signées, donc irréversible (Forme J). Le jour où un producteur en scelle un, il ajoute son
 * entrée, et le type l'y oblige.
 */
export const PIECE_TYPE_CODES = {
  ticket: 'TIK',
  facture: 'FAC',
  avoir: 'AVO',
  justificatif: 'JDP',
} as const satisfies Partial<Record<PieceType, string>>;

/** Un type de pièce qu'un producteur sait sceller. */
export type SealablePieceType = keyof typeof PIECE_TYPE_CODES;

/**
 * LE VOCABULAIRE COMPLET DE LA COLONNE — liste FERMÉE.
 *
 * ⚠️ 🔴 IL CONTIENT DEUX CODES QU'AUCUN PRODUCTEUR N'ÉCRIT : `NOTE` et `FTK` (facture sur papier
 * ticket, famille R12). Ils sont documentés au schéma et déjà LUS par des écrans, donc ils
 * appartiennent au vocabulaire même si rien ne les pose. **Mesuré sur les cinq bases de la bêta : seuls
 * `TIK`, `FAC`, `AVO` et `JDP` existent réellement** — les lister quand même est ce qui permet à un
 * lecteur d'être exhaustif sans deviner.
 */
export const PIECE_TYPE_VOCABULARY: readonly string[] = ['NOTE', 'TIK', 'JDP', 'FAC', 'FTK', 'AVO'];

/**
 * LE CODE PERSISTÉ DE CHAQUE TYPE D'OPÉRATION QU'UN PRODUCTEUR SCELLE.
 *
 * ⚠️ MÊME RÈGLE : `OperationType` compte huit membres, cinq sont produits (`vente`, `retour_total`,
 * `annulation`, `changement_reglement`, `tresorerie`). `retour_partiel`, `echange` et `avoir` n'ont aucun producteur ;
 * on ne leur invente pas de code.
 * ⚠️ `FOR` figure au commentaire du schéma sans producteur ni sens écrit nulle part : il n'entre donc
 * dans aucune table ici — une correspondance qu'on ne sait pas justifier ne se déclare pas.
 */
export const OPERATION_TYPE_CODES = {
  vente: 'VEN',
  retour_total: 'RET',
  annulation: 'ANN',
  changement_reglement: 'CHG',
  // ⚠️ L'ÉMISSION D'UN TITRE (chèque cadeau, acompte) : une opération de TRÉSORERIE, hors grands totaux (§ 6.18.1).
  //    Code choisi une fois pour toutes (Forme J : il entre dans des pièces signées).
  tresorerie: 'TRE',
} as const satisfies Partial<Record<OperationType, string>>;

/** Un type d'opération qu'un producteur sait sceller. */
export type SealableOperationType = keyof typeof OPERATION_TYPE_CODES;

/**
 * Le code à STOCKER pour le type de pièce qu'on vient de SIGNER.
 * ⚠️ 🔴 C'EST LE SEUL GESTE QUI RELIE LES DEUX FORMES : l'appelant choisit son type de pièce UNE fois,
 * le signe avec, et en dérive le code stocké. Les deux ne peuvent plus se contredire.
 */
export const pieceTypeCode = (t: SealablePieceType): string => PIECE_TYPE_CODES[t];

/** Le code à STOCKER pour le type d'opération qu'on vient de SIGNER. */
export const operationTypeCode = (t: SealableOperationType): string => OPERATION_TYPE_CODES[t];

/**
 * LES TYPES DE PIÈCE DE LA FAMILLE FACTURE (R12).
 * ⚠️ `FTK` en fait partie : c'est une facture émise sur papier ticket, pas un ticket.
 */
export const INVOICE_PIECE_TYPES: readonly string[] = ['FAC', 'FTK'];

/**
 * CETTE PIÈCE REND-ELLE DE L'ARGENT AU CLIENT ?
 *
 * ⚠️ 🔴 DEUX COLONNES, UNE SEULE QUESTION, et les deux sont nécessaires : un remboursement au comptoir
 * se reconnaît à son TYPE (`AVO`), un retour sur pièce d'origine à son OPÉRATION (`RET`). Mesuré sur la
 * base réelle : `AVO|RET` et `AVO|ANN` coexistent, donc tester une seule colonne raterait des cas.
 * La règle vivait en double — une fois en SQL au serveur, une fois en TypeScript à l'écran.
 */
export const isRefundPiece = (pieceType: unknown, operationType: unknown): boolean =>
  String(pieceType ?? '') === PIECE_TYPE_CODES.avoir
  || String(operationType ?? '') === OPERATION_TYPE_CODES.retour_total;

/** Cette pièce appartient-elle à la famille FACTURE (R12) ? */
export const isInvoicePiece = (pieceType: unknown): boolean =>
  INVOICE_PIECE_TYPES.includes(String(pieceType ?? ''));

/**
 * CE DOCUMENT EST-IL UNE FACTURE, ou une FACTURE D'AVOIR ? — son code de type (une facture, y compris reprise) ou son
 * numéro dans la série des factures (la facture d'avoir porte le type `AVO` des avoirs, mais un numéro `F-`).
 * ⚠️ LE TYPE SEUL NE SUFFIT PAS : `AVO` ne distingue pas un avoir de ticket d'une facture d'avoir. C'est la règle de
 *    tout ce qui choisit un gabarit, un mode d'impression ou une série à partir d'une pièce stockée.
 */
export const isInvoiceDocument = (pieceType: unknown, pieceNumber: string): boolean =>
  isInvoicePiece(pieceType) || isInvoiceSeriesNumber(pieceNumber);
